import { netTermsToString } from "../../infra/utils";
import { ActivityLogAgreementState, ActivityLogChargeState, ActivityLogInvoiceState, ActivityLogPMIntegrationName, ActivityLogPaymentMethodType, ActivityLogServiceState, relationshipModel, serviceBillingModel, serviceCostModel, serviceModel } from "../../models";
import { utils } from "../../utils";
import { getAllowManualPaymentsChangeText, getAmendmentsApprovalMethodChangeBullet, getCoverCCFeesChangeText, getInvoicesApprovalMethodChangeBullet } from "../../business-logic/amendmentsLogic";
import { compact } from 'lodash-es';
import { AgreementsLine, EditLine, InvoicesLine, MailSentLine18, PaymentsLine, PlayIcon } from 'seagull/icons';
import { RichTextViewer, TextButton, useOverlayContext } from 'seagull';
import { Link } from 'react-router-dom';
import { routes } from "../../constants";
import { getURLWithFullPageInvoice } from "../FullPageInvoice/FullPageInvoice.utils";
import { currencyUtils } from "../../utils/currency.utils";
import { ClientPreview } from "../ClientPreview/ClientPreview";
import { UserRole } from "../../models";
import pluralize from "../../utils/pluralize";
import { Accounting } from 'anchor-scout-common/types';
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
export let Placeholder = /*#__PURE__*/function (Placeholder) {
  Placeholder["InvoiceLink"] = "[invoiceLink]";
  Placeholder["AgreementLink"] = "[agreementLink]";
  Placeholder["AgreementLinkUppercase"] = "[agreementLinkUppercase]";
  Placeholder["ManagePaymentDetails"] = "[managePaymentDetails]";
  Placeholder["ServiceDescription"] = "[serviceDescription]";
  Placeholder["OriginalService"] = "[originalService]";
  return Placeholder;
}({});
export function getIcon(entry) {
  return entry.accept({
    whenAgreement: entry => entry.isReminderEntry() ? /*#__PURE__*/_jsx(MailSentLine18, {}) : /*#__PURE__*/_jsx(AgreementsLine, {}),
    whenCharge: () => /*#__PURE__*/_jsx(PaymentsLine, {}),
    whenInvoice: () => /*#__PURE__*/_jsx(InvoicesLine, {}),
    whenService: entry => entry.isDiscountExpirationEntry() ? /*#__PURE__*/_jsx(PlayIcon, {}) : /*#__PURE__*/_jsx(EditLine, {})
  });
}
export function getContent(config) {
  const textContent = getContentText(config);
  return replacePlaceholders(textContent, [[Placeholder.AgreementLink, () => /*#__PURE__*/_jsx(AgreementLink, {
    config: config
  }, "agreementLink")], [Placeholder.AgreementLinkUppercase, () => /*#__PURE__*/_jsx(AgreementLink, {
    uppercase: true,
    config: config
  }, "agreementLinkUppercase")], [Placeholder.ManagePaymentDetails, () => /*#__PURE__*/_jsx(TextButton, {
    as: Link,
    to: routes.paymentMethods(config.relationship.id),
    "data-testid": config.testid && "".concat(config.testid, "-pm-link"),
    underline: "none",
    children: "Manage payment details"
  }, "managePaymentDetails")], [Placeholder.InvoiceLink, () => {
    const {
      relationship,
      testid,
      entry,
      logBI
    } = config;
    return entry.isInvoiceEntry() && /*#__PURE__*/_jsx(TextButton, {
      as: Link,
      to: getURLWithFullPageInvoice({
        invoiceId: entry.invoiceId(),
        relationshipId: relationship.id
      }),
      onClick: () => logBI(10575, {
        invoiceId: entry.invoiceId()
      }),
      underline: "none",
      "data-testid": testid && "".concat(testid, "-invoice-link"),
      children: /*#__PURE__*/_jsx("span", {
        children: entry.formattedNumber()
      })
    }, "invoiceLink");
  }], [Placeholder.ServiceDescription, () => {
    const {
      entry,
      testid
    } = config;
    return entry.isServiceEntry() && /*#__PURE__*/_jsx(RichTextViewer, {
      "data-testid": testid && "".concat(testid, "-service-description"),
      value: entry.descriptionChange() || undefined
    });
  }], [Placeholder.OriginalService, () => /*#__PURE__*/_jsx(OriginalProposalLink, {
    config: config
  })]]);
}
export function getContentText(config) {
  const {
    entry
  } = config;
  return entry.accept({
    whenAgreement: entry => getAgreementContent(config, entry),
    whenCharge: entry => getChargeContent(entry),
    whenInvoice: entry => getInvoiceContent(config, entry),
    whenService: entry => getServiceContent(entry)
  });
}
const userDetails = user => "".concat(user.name).concat(user.role === UserRole.Accountant ? " (".concat(user.email, ")") : '');
function getAgreementContent(config, entry) {
  const {
    isVendor
  } = config;
  const vendorName = entry.vendorName() || '';
  const customer = entry.customer();
  const vendor = entry.vendor();
  const customerName = entry.customerName() || '';
  const paymentMethod = entry.paymentMethod();
  const customerBusinessName = relationshipModel.customerName(config.relationship);
  const channel = entry.channel();
  switch (entry.state()) {
    case ActivityLogAgreementState.Sent:
      {
        const reviewersText = entry.hasReviewers() ? " and the following additional recipients: ".concat(entry.reviewerNamesAndEmails().join(', ')) : '';
        return "".concat(Placeholder.AgreementLinkUppercase, " sent to ").concat(entry.customerNameAndEmail()).concat(reviewersText, ". ").concat(isVendor ? Placeholder.OriginalService : '');
      }
    case ActivityLogAgreementState.Reviewed:
      return "".concat(Placeholder.AgreementLinkUppercase, " was reviewed by ").concat(customerName, ".");
    case ActivityLogAgreementState.MicroDepositStarted:
    case ActivityLogAgreementState.MicroDeposit:
      return "".concat(customerName, " initiated a micro-deposit verification.");
    case ActivityLogAgreementState.MicroDepositStartedV2:
      return "Micro-deposit verification process for bank account (".concat((paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.last4) || '****', ") has been initiated.");
    case ActivityLogAgreementState.MicroDepositFailed:
      return "Micro-deposit verification failed.";
    case ActivityLogAgreementState.MicroDepositFailedV2:
      return "Bank account (".concat((paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.last4) || '****', ") failed verification. ").concat(Placeholder.ManagePaymentDetails, ".");
    case ActivityLogAgreementState.MicroDepositVerified:
      return "Micro-deposit was verified by ".concat(customerName || vendorName, ".");
    case ActivityLogAgreementState.MicroDepositVerifiedV2:
      return "Bank account (".concat((paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.last4) || '****', ") was verified and connected successfully.");
    case ActivityLogAgreementState.PaymentMethod:
      return "".concat(customerName || vendorName, " added a payment method.");
    case ActivityLogAgreementState.Approved:
      return "".concat(Placeholder.AgreementLinkUppercase, " approved by ").concat(customerName, ".");
    case ActivityLogAgreementState.Terminated:
      return "".concat(Placeholder.AgreementLinkUppercase, " terminated by ").concat(vendorName, ".");
    case ActivityLogAgreementState.Changed:
      {
        const netTerms = entry.netTermsChanges();
        const netTermsText = netTerms != null ? "Payment terms: ".concat(netTermsToString(netTerms)) : '';
        return "".concat(Placeholder.AgreementLinkUppercase, " was edited by ").concat(vendorName, " and now includes the following:\n").concat(netTermsText);
      }
    case ActivityLogAgreementState.ChangesApproved:
      return "The latest amendments have been approved by ".concat(customerName, ".");
    case ActivityLogAgreementState.ChangesAutoApproved:
      return "As per the agreement terms, the latest amendments are effective as of today.";
    case ActivityLogAgreementState.StoppedAutoApproval:
      return "".concat(customerName, " declined the auto-approval of the latest amendments.");
    case ActivityLogAgreementState.SigningReminderSent:
      return "Anchor sent an automatic ".concat(channel, " reminder to ").concat(customerName, ", to approve the proposal.");
    case ActivityLogAgreementState.SigningReminderSentManual:
      return "".concat(vendorName, " sent an ").concat(channel, " reminder to ").concat(customerName, ", to approve the proposal.");
    case ActivityLogAgreementState.MicroDepositReminderSent:
      return "Anchor sent an automatic micro deposit verification reminder to ".concat(customerName, ".");
    case ActivityLogAgreementState.PaymentMethodChanged:
      {
        const user = customer || vendor;
        if (!paymentMethod || !user) {
          return null;
        }
        const performedByVendor = !customer || user.role === UserRole.Accountant;
        const pmType = paymentMethod.type === ActivityLogPaymentMethodType.CreditCard ? 'Credit card' : 'Bank account';
        return "The ".concat(Placeholder.AgreementLink, " has been updated with a new payment method - ").concat(pmType, " ending in ").concat(paymentMethod.last4, " by ").concat(userDetails(user)).concat(performedByVendor ? ", after confirming they have full authorization from ".concat(customerBusinessName) : '', ".");
      }
    case ActivityLogAgreementState.PaymentMethodDisconnected:
      {
        if (!paymentMethod) {
          return null;
        }
        const pmType = paymentMethod.type === ActivityLogPaymentMethodType.CreditCard ? 'credit card' : 'bank account';
        return "The payment method (".concat(pmType, " ending in ").concat(paymentMethod.last4, ") connected to the agreement has been disconnected by ").concat(vendor ? "".concat(userDetails(vendor)) : '', ".");
      }
    case ActivityLogAgreementState.SignedExternally:
      return "Existing agreement was uploaded and authorized for billing on Anchor by ".concat(vendorName, ".");
    case ActivityLogAgreementState.RequestedPaymentMethod:
      {
        if (!customer) return null;
        return "A request to add a payment method has been sent to ".concat(customer.name, " (").concat(customer.email, ")");
      }
    case ActivityLogAgreementState.LegalTermsUpdated:
      return "Terms & Conditions have been updated.";
    case ActivityLogAgreementState.AmendmentsApprovalMethodUpdated:
      {
        const approvalMethodChanges = entry.amendmentsApprovalMethodChanges();
        if (!approvalMethodChanges) return null;
        const bullet = getAmendmentsApprovalMethodChangeBullet(approvalMethodChanges.before, approvalMethodChanges.after);
        return bullet ? "".concat(bullet.text).concat(vendorName ? " by ".concat(vendorName) : '', ".") : null;
      }
    case ActivityLogAgreementState.InvoicesApprovalMethodUpdated:
      {
        const approvalMethodChanges = entry.invoicesApprovalMethodChanges();
        if (!approvalMethodChanges) return null;
        const bullet = getInvoicesApprovalMethodChangeBullet(approvalMethodChanges.before, approvalMethodChanges.after);
        return bullet ? "".concat(bullet.text).concat(vendorName ? " by ".concat(vendorName) : '', ".") : null;
      }
    case ActivityLogAgreementState.TitleUpdated:
      {
        const changesTitle = entry.titleChanges();
        return changesTitle ? "".concat(vendorName, " renamed this ").concat(Placeholder.AgreementLink, " \"").concat(changesTitle.after, "\"") : null;
      }
    case ActivityLogAgreementState.ManualPaymentsUpdated:
      {
        const changesManualPayments = entry.manualPaymentsChanges();
        if (!changesManualPayments) return null;
        const {
          before,
          after
        } = changesManualPayments;
        return "".concat(getAllowManualPaymentsChangeText(before, after), " by ").concat(vendorName, ".");
      }
    case ActivityLogAgreementState.CoverCreditCardFeesUpdated:
      {
        const changesCoverCCFees = entry.coverCCFeesChanges();
        if (!changesCoverCCFees) return null;
        const changeText = getCoverCCFeesChangeText(changesCoverCCFees.before, changesCoverCCFees.after, relationshipModel.vendorName(config.relationship), relationshipModel.customerName(config.relationship));
        return "".concat(changeText, " by ").concat(vendorName);
      }
    case ActivityLogAgreementState.SellerUpdated:
      {
        const changesSeller = entry.sellerChanges();
        if (!changesSeller) return null;
        return "Assignee changed from ".concat(changesSeller.before, " to ").concat(changesSeller.after, " by ").concat(vendorName, ".");
      }
    case ActivityLogAgreementState.PracticeManagementContactCreated:
      {
        const integrationName = entry.integrationName();
        return integrationName ? "Contact automatically created in ".concat(integrationNameToText[integrationName], ".") : null;
      }
    case ActivityLogAgreementState.PracticeManagementItemsCreated:
      {
        const integrationName = entry.integrationName();
        const integrationServices = entry.integrationServices();
        return integrationName && integrationServices !== null && integrationServices !== void 0 && integrationServices.length ? ["Items automatically created in ".concat(integrationNameToText[integrationName], " for the following services:\n")].concat(integrationServices.map(service => "\u2022 ".concat(service.name))).join('\n') : null;
      }
    case ActivityLogAgreementState.SentSilently:
      {
        return "Proposal created by ".concat(vendorName, ". ").concat(isVendor ? Placeholder.OriginalService : '');
      }
    case ActivityLogAgreementState.MicroDepositRestarted:
      {
        return "Micro-deposit verification process for bank account (".concat((paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.last4) || '****', ") has been restarted by ").concat(customerName || vendorName, ".");
      }
    case ActivityLogAgreementState.AmendmentApprovalReminderSent:
      {
        return "Anchor sent an automatic email reminder to ".concat(customerBusinessName, " to approve the amendments to the agreement.");
      }
    default:
      return null;
  }
}
const integrationNameToText = {
  [ActivityLogPMIntegrationName.Karbon]: 'Karbon',
  [ActivityLogPMIntegrationName.ClientHub]: 'Client Hub',
  [ActivityLogPMIntegrationName.FinancialCents]: 'Financial Cents',
  [ActivityLogPMIntegrationName.Keeper]: 'Double'
};
function getChargeContent(entry) {
  const chargeName = entry.name();
  const vendorName = entry.vendorName() || '';
  const customerName = entry.customerName() || '';
  switch (entry.state()) {
    case ActivityLogChargeState.Created:
      {
        const chargeNote = entry.note();
        const noteText = chargeNote ? ", with the following note: ".concat(chargeNote) : '';
        return "A new charge, ".concat(chargeName, ", with a price of ").concat(entry.formattedPrice(), " was added by ").concat(vendorName).concat(noteText, ".");
      }
    case ActivityLogChargeState.Approved:
      return "A charge, ".concat(chargeName, ", with a price of ").concat(entry.formattedPrice(), ", was approved by ").concat(customerName, ".");
    case ActivityLogChargeState.Canceled:
      return "The charge, ".concat(chargeName, ", with a price of ").concat(entry.formattedPrice(), ", was canceled by ").concat(vendorName, ".");
    default:
      return null;
  }
}
function getInvoiceContent(config, entry) {
  const {
    relationship,
    isVendor
  } = config;
  const vendorName = entry.vendorName() || '';
  const customerName = entry.customerName() || '';
  const vendorBusinessName = relationshipModel.vendorName(relationship);
  const customerBusinessName = relationshipModel.customerName(relationship);
  switch (entry.state()) {
    case ActivityLogInvoiceState.AccountingInvoiceResyncRequested:
      return "Invoice ".concat(Placeholder.InvoiceLink, " ").concat(entry.integrationType() === Accounting.Xero ? 'Payment ' : '', "was requested for re-sync to ").concat(entry.integrationFullName(), " by ").concat(vendorName, ".");
    case ActivityLogInvoiceState.AccountingInvoiceSynced:
      return "Invoice ".concat(Placeholder.InvoiceLink, " synced to ").concat(entry.integrationFullName(), ".");
    case ActivityLogInvoiceState.AccountingPaymentSynced:
      return "Invoice ".concat(Placeholder.InvoiceLink, " payment synced to ").concat(entry.integrationFullName(), ".");
    case ActivityLogInvoiceState.Voided:
      return "Invoice ".concat(Placeholder.InvoiceLink, " was voided by ").concat(vendorName, ".");
    case ActivityLogInvoiceState.PaymentStoppedOLD:
      return "Invoice ".concat(Placeholder.InvoiceLink, " was disputed by ").concat(customerName, ".");
    case ActivityLogInvoiceState.PaymentStopped:
      return "A payment for invoice ".concat(Placeholder.InvoiceLink, " was stopped by ").concat(customerName, " from ").concat(customerBusinessName, " with the following note: \"").concat(entry.paymentStoppedReason() || '', "\".");
    case ActivityLogInvoiceState.PaymentResumed:
      return "A payment for invoice ".concat(Placeholder.InvoiceLink, " was resumed by ").concat(customerName, " from ").concat(customerBusinessName, ".");
    case ActivityLogInvoiceState.Completed:
      return "Invoice ".concat(Placeholder.InvoiceLink, " was finalized (added hours/units) by ").concat(vendorName, ".");
    case ActivityLogInvoiceState.IssuedIncomplete:
      return "Invoice ".concat(Placeholder.InvoiceLink, " was issued but has missing quantity/hours.");
    case ActivityLogInvoiceState.Issued:
      return "Invoice ".concat(Placeholder.InvoiceLink, " was issued.");
    case ActivityLogInvoiceState.PaymentFailed:
      return "Invoice ".concat(Placeholder.InvoiceLink, ": Payment failed.");
    case ActivityLogInvoiceState.PaymentRetried:
      return "Invoice ".concat(Placeholder.InvoiceLink, " was charged manually.");
    case ActivityLogInvoiceState.PaymentRetriedByAdmin:
      return "Invoice ".concat(Placeholder.InvoiceLink, " payment was retried by an Anchor admin.");
    case ActivityLogInvoiceState.PaymentAutoRetried:
      return "Invoice ".concat(Placeholder.InvoiceLink, " payment was automatically retried.");
    case ActivityLogInvoiceState.PaymentRetriedByCustomer:
      return "Invoice ".concat(Placeholder.InvoiceLink, " payment was retried by ").concat(customerName, " from ").concat(customerBusinessName, ".");
    case ActivityLogInvoiceState.PaymentRetriedByVendor:
      return "Invoice ".concat(Placeholder.InvoiceLink, " payment was retried by ").concat(vendorName, " from ").concat(vendorBusinessName, ".");
    case ActivityLogInvoiceState.Paid:
      return "Invoice ".concat(Placeholder.InvoiceLink, " was paid.");
    case ActivityLogInvoiceState.PaidExternally:
      return "Invoice ".concat(Placeholder.InvoiceLink, " was marked as paid.");
    case ActivityLogInvoiceState.DisputeResolved:
      return "A payment for invoice ".concat(Placeholder.InvoiceLink, " has been stopped by ").concat(customerName, " from ").concat(customerBusinessName, ".");
    case ActivityLogInvoiceState.Approved:
      return "Invoice ".concat(Placeholder.InvoiceLink, " was approved by ").concat(customerName, ".");
    case ActivityLogInvoiceState.PaymentAutoApproved:
      return "As per the agreement terms, invoice ".concat(Placeholder.InvoiceLink, " has been auto-approved.");
    case ActivityLogInvoiceState.AutoApprovalDisabled:
      return "The auto approval of invoice ".concat(Placeholder.InvoiceLink, " has been disabled by ").concat(customerName, ".");
    case ActivityLogInvoiceState.Resent:
      return "Invoice ".concat(Placeholder.InvoiceLink, " resent by ").concat(vendorName, ".");
    case ActivityLogInvoiceState.PaymentConfirmed:
      return "Invoice ".concat(Placeholder.InvoiceLink, " payment was confirmed.");
    case ActivityLogInvoiceState.PaymentProcessing:
      return "Invoice ".concat(Placeholder.InvoiceLink, " payment was processed.");
    case ActivityLogInvoiceState.Refunded:
      {
        if (entry.isFullRefund()) {
          if (isVendor) return "".concat(vendorName, " refunded ").concat(entry.totalAmount().display(), " from invoice ").concat(Placeholder.InvoiceLink, " with the following note: \"").concat(entry.refundRequestedReason() || '', "\". (note visible only to you)");
          return "".concat(vendorName, " refunded ").concat(entry.totalAmount().display(), " from invoice ").concat(Placeholder.InvoiceLink, ".");
        } else if (entry.haveFees()) {
          if (isVendor) return "".concat(vendorName, " partially refunded ").concat(entry.totalRefundedAmount().display(), " (including processing fees reimbursement) from invoice ").concat(Placeholder.InvoiceLink, " with the following note: \"").concat(entry.refundRequestedReason() || '', "\". (note visible only to you)");
          return "".concat(vendorName, " partially refunded ").concat(entry.totalRefundedAmount().display(), " (including processing fees reimbursement) from invoice ").concat(Placeholder.InvoiceLink, ".");
        } else {
          if (isVendor) return "".concat(vendorName, " partially refunded ").concat(entry.totalRefundedAmount().display(), " from invoice ").concat(Placeholder.InvoiceLink, " with the following note: \"").concat(entry.refundRequestedReason() || '', "\". (note visible only to you)");
          return "".concat(vendorName, " partially refunded ").concat(entry.totalRefundedAmount().display(), " from invoice ").concat(Placeholder.InvoiceLink, ".");
        }
      }
    case ActivityLogInvoiceState.PaymentDisputed:
      return "Invoice ".concat(Placeholder.InvoiceLink, ": Payment disputed.");
    case ActivityLogInvoiceState.PayNowRequested:
      return "Invoice ".concat(Placeholder.InvoiceLink, " has been requested to be paid now by ").concat(customerName, ".");
    case ActivityLogInvoiceState.RefundRequested:
      return null;
    case ActivityLogInvoiceState.RefundFailed:
      {
        if (entry.isFullRefund()) {
          return "A refund for invoice ".concat(Placeholder.InvoiceLink, " for ").concat(entry.totalAmount().display(), " has failed.");
        } else if (entry.haveFees()) {
          return "A partial refund for invoice ".concat(Placeholder.InvoiceLink, " for ").concat(entry.totalRefundedAmount().display(), " (including processing fees reimbursement) has failed.");
        } else {
          return "A partial refund for invoice ".concat(Placeholder.InvoiceLink, " for ").concat(entry.totalRefundedAmount().display(), " has failed.");
        }
      }
    default:
      return null;
  }
}
function getServiceContent(entry) {
  const vendorName = entry.vendorName() || '';
  const customerName = entry.customerName() || '';
  const serviceName = entry.name();
  switch (entry.state()) {
    case ActivityLogServiceState.Approved:
      return "The addition of ".concat(serviceName, " service was approved by ").concat(customerName, ".");
    case ActivityLogServiceState.ChangesApproved:
      return "An amendment to ".concat(serviceName, " service was approved by ").concat(customerName, ".");
    case ActivityLogServiceState.ChangesAutoApproved:
      return "As per the agreement terms, the amendments to ".concat(serviceName, " service are effective as of today.");
    case ActivityLogServiceState.Resumed:
      return "".concat(vendorName, " resumed the ").concat(serviceName, " service.");
    case ActivityLogServiceState.Paused:
      return "".concat(vendorName, " paused the ").concat(serviceName, " service.");
    case ActivityLogServiceState.Created:
      {
        const serviceChangesCost = entry.serviceChangesCost();
        return compact(["A new service was added by ".concat(vendorName, "."), "The ".concat(serviceName, " service was added and includes the following:"), getServiceDescription(entry.descriptionChange()), getServicePricePerUnitText(serviceChangesCost), getServiceDiscountText(serviceChangesCost), getServiceCap(serviceChangesCost), getServiceRecurrenceText(entry), getServiceNote(entry.note())]).join('\n');
      }
    case ActivityLogServiceState.Changed:
      {
        const serviceChangesCost = entry.serviceChangesCost();
        return compact(["An existing service was edited by ".concat(vendorName, "."), "The ".concat(serviceName, " service was edited and now includes the following:"), getServiceDescription(entry.descriptionChange()), getServicePricePerUnitText(serviceChangesCost), getServiceDiscountText(serviceChangesCost), getServiceCap(serviceChangesCost), getServiceRecurrenceText(entry), getServiceNote(entry.note())]).join('\n');
      }
    case ActivityLogServiceState.Terminated:
      return "Service ".concat(serviceName, " has been stopped by ").concat(vendorName);
    case ActivityLogServiceState.Canceled:
      return "Service ".concat(serviceName, " has been canceled by ").concat(vendorName);
    case ActivityLogServiceState.BillCommandDeliverablesPendingApproval:
      return "Delivery of ".concat(serviceName, " was sent by ").concat(vendorName, ".");
    case ActivityLogServiceState.BillCommandDeliverablesApproved:
      return "Delivery of ".concat(serviceName, " was approved by ").concat(customerName, ".");
    case ActivityLogServiceState.BillCommandDeliverablesRejected:
      return "Delivery of ".concat(serviceName, " was rejected by ").concat(customerName, ".");
    case ActivityLogServiceState.BillCommandDeliverablesWithdrawn:
      return "Delivery of ".concat(serviceName, " was withdrawn by ").concat(vendorName, ".");
    case ActivityLogServiceState.ChangesDiscarded:
      return "Amendment of ".concat(serviceName, " was discarded by ").concat(vendorName, ".");
    case ActivityLogServiceState.PriceIncreased:
      {
        const changesPrice = entry.priceChange();
        const beforeChangesPrice = entry.priceBeforeChange();
        return changesPrice && beforeChangesPrice ? "As per the agreement terms, the price of \u201C".concat(serviceName, "\u201D automatically increased from ").concat(currencyUtils.formatDollarAmount(beforeChangesPrice), " to ").concat(currencyUtils.formatDollarAmount(changesPrice), ".") : null;
      }
    case ActivityLogServiceState.IntegratedClassChanged:
      {
        const defaultText = 'the default';
        const before = entry.integratedClassNameBeforeChange() || defaultText;
        const after = entry.integratedClassNameChange() || defaultText;
        return "The class of service ".concat(serviceName, " has been changed from ").concat(before, " to ").concat(after, " by ").concat(vendorName, ".");
      }
    case ActivityLogServiceState.ServiceDiscountExpired:
      {
        return "As per the agreement terms, the discount on ".concat(serviceName, " has expired");
      }
    default:
      return null;
  }
}
function replacePlaceholders(content, replacers) {
  if (!content) {
    return null;
  }
  let result = [content];
  replacers.forEach(replacer => {
    result = replacePlaceholder(result, replacer);
  });
  return /*#__PURE__*/_jsx(_Fragment, {
    children: result
  });
}
function replacePlaceholder(content, _ref) {
  let [placeholder, getReplacedContent] = _ref;
  return content.map(item => {
    if (typeof item !== 'string') {
      return item;
    }
    const splitted = item.split(placeholder);
    if (splitted.length === 1) {
      return item;
    }
    const result = [];
    splitted.forEach((item, index) => {
      result.push(item);
      if (index < splitted.length - 1) {
        result.push(getReplacedContent());
      }
    });
    return compact(result);
  }).flat(1);
}
function getServiceDescription(description) {
  return description ? "Description:\n".concat(Placeholder.ServiceDescription) : '';
}
function getServiceNote(note) {
  return note ? "Note: ".concat(note) : '';
}
function getServiceRecurrenceText(entry) {
  if (!entry.hasChanges()) {
    return '';
  }
  const billing = entry.billingChange();
  const isRecurring = !!billing && serviceBillingModel.isRecurring(billing);
  const recurrenceText = isRecurring ? " with ".concat(serviceModel.recurrencePeriod(billing), " billing") : '';
  const dayOfWeek = billing ? serviceBillingModel.billingDayOfWeek(billing) : null;
  const dayOfMonth = billing ? serviceBillingModel.billingDayOfMonth(billing) : null;
  const dayText = dayOfWeek ? " (on ".concat(dayOfWeek, ")") : dayOfMonth ? " (on the ".concat(dayOfMonth.formatDay('last day'), " of each month)") : '';
  return "The service will be ".concat(isRecurring ? 'an ongoing' : 'a one-time', " service").concat(recurrenceText).concat(dayText, ".");
}
function getServicePricePerUnitText(cost) {
  const price = cost && serviceModel.price({
    cost
  });
  if (price) {
    const formattedPrice = utils.formatDollarAmount(price);
    if (serviceCostModel.isVariablePrice(cost)) {
      return "Price: ".concat(formattedPrice, "/").concat(serviceCostModel.formattedUnitName(cost));
    } else if (serviceCostModel.isRangePrice(cost)) {
      const minPrice = serviceModel.minPrice({
        cost
      });
      return "Price: ".concat(minPrice ? utils.formatDollarAmount(minPrice) : '0', "-").concat(formattedPrice, " range price");
    } else {
      return "Price: ".concat(formattedPrice, " fixed price");
    }
  } else {
    return '';
  }
}
function getServiceDiscountText(cost) {
  const discount = cost && serviceModel.discount({
    cost
  });
  return discount ? "Discount of ".concat(discount.format()) : '';
}
function getServiceCap(cost) {
  const unitCap = cost && serviceModel.unitCap({
    cost
  });
  return unitCap ? "Preapproved: ".concat(utils.formatNumberWithCommas(unitCap), " ").concat(pluralize(serviceCostModel.formattedUnitName(cost), unitCap.toNumber())) : '';
}
function AgreementLink(props) {
  const {
    uppercase,
    config: {
      relationship,
      logBI,
      testid
    }
  } = props;
  return /*#__PURE__*/_jsx(TextButton, {
    as: Link,
    to: routes.agreement(relationship.id),
    onClick: () => logBI(10576),
    "data-testid": testid && "".concat(testid, "-agreement-link"),
    underline: "none",
    children: uppercase ? 'Agreement' : 'agreement'
  });
}
const OriginalProposalLink = props => {
  const {
    config: {
      relationship,
      testid
    }
  } = props;
  const overlayContext = useOverlayContext();
  return /*#__PURE__*/_jsx(TextButton, {
    onClick: () => {
      overlayContext.showModal({
        closeOnClickOutside: true,
        closeOnEscapeKey: true,
        content: _ref2 => {
          let {
            dismissModal,
            registerCanDismiss
          } = _ref2;
          return /*#__PURE__*/_jsx(ClientPreview, {
            type: "originalProposal",
            relationshipId: relationship.id,
            dismissModal: dismissModal,
            registerCanDismiss: registerCanDismiss
          });
        }
      });
    },
    "data-testid": testid && "".concat(testid, "-view-original-proposal"),
    underline: "none",
    children: 'View original proposal'
  });
};